import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchAppointments,
  fetchBusinessHours,
  fetchAvailabilityExceptions,
  fetchAppointmentTypes,
  fetchSchedulingSettings,
} from '../api';
import AppointmentModal from './appointmentmodal';
import SchedulingSettingsModal from './schedulingsettingsmodal';
import './schedulingcalendar.css';

const DAY_START_HOUR = 7;
const DAY_END_HOUR = 19;
const ROW_MINUTES = 30;
const ROWS_PER_HOUR = 60 / ROW_MINUTES;
const BASE_ROW_HEIGHT = 30; // px per row when only one appointment occupies it
const STACK_GAP = 3; // px of clear space forced between stacked appointment blocks
const TOTAL_ROWS = (DAY_END_HOUR - DAY_START_HOUR) * ROWS_PER_HOUR;

// Counts, per half-hour row, how many of a day's appointments overlap that row's window —
// used to decide how tall each row needs to be so simultaneous appointments never overlap.
const countRowOverlaps = (dayAppointments) => {
  const counts = new Array(TOTAL_ROWS).fill(0);
  dayAppointments.forEach(({ start, end }) => {
    const startMin = (start.getHours() * 60 + start.getMinutes()) - DAY_START_HOUR * 60;
    const endMin = (end.getHours() * 60 + end.getMinutes()) - DAY_START_HOUR * 60;
    const firstRow = Math.max(0, Math.floor(startMin / ROW_MINUTES));
    const lastRow = Math.min(TOTAL_ROWS - 1, Math.ceil(endMin / ROW_MINUTES) - 1);
    for (let r = firstRow; r <= lastRow; r += 1) {
      counts[r] += 1;
    }
  });
  return counts;
};

// Shop is never open Saturday/Sunday — the calendar only ever shows the 5 business days.
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const BUSINESS_DAYS_PER_WEEK = 5;
const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};
const VIEW_MODES = [
  { key: 'week', label: 'Week' },
  { key: 'day', label: 'Day' },
  { key: 'list', label: 'List' },
];
// Below this width the week grid can't fit — those screens only get Day and List.
const MOBILE_BREAKPOINT = 768;
const getIsMobile = () => (typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false);

const startOfWeek = (date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

const toISODate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const minutesSinceMidnight = (dateObj) => dateObj.getHours() * 60 + dateObj.getMinutes();

const STATUS_COLORS = {
  scheduled: 'rgb(15, 15, 138)',
  arrived: '#2e7d32',
  cancelled: '#9e9e9e',
};

// Arrived/cancelled appointments stay on the calendar (never removed) but shaded, so
// only genuinely upcoming scheduled appointments read at full strength.
const SHADED_STATUSES = ['arrived', 'cancelled'];

// Each service's color is assigned once and stored on the AppointmentType itself
// (backend/scheduling/models.py — auto-assigned on creation, editable in Settings),
// so it stays stable regardless of other services being added, removed, or reordered.
const buildTypeColorMap = (appointmentTypes) => {
  const map = {};
  appointmentTypes.forEach((t) => {
    map[t.id] = t.color || '#666';
  });
  return map;
};

const SchedulingCalendar = () => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [viewMode, setViewMode] = useState(() => (getIsMobile() ? 'day' : 'week'));
  const [anchorDate, setAnchorDate] = useState(() => {
    let today = new Date();
    if (getIsMobile()) {
      while (isWeekend(today)) today = addDays(today, 1);
    }
    return today;
  });
  const [appointments, setAppointments] = useState([]);
  const [businessHours, setBusinessHours] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [schedulingSettings, setSchedulingSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  const [showTypeFilter, setShowTypeFilter] = useState(false);

  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [prefilledStart, setPrefilledStart] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const weekStart = startOfWeek(anchorDate);
  const weekDays = Array.from({ length: BUSINESS_DAYS_PER_WEEK }, (_, i) => addDays(weekStart, i));
  const dayColumns = viewMode === 'day' ? [anchorDate] : weekDays;
  const rangeFrom = viewMode === 'day' ? toISODate(anchorDate) : toISODate(weekStart);
  const rangeTo = viewMode === 'day' ? toISODate(anchorDate) : toISODate(addDays(weekStart, BUSINESS_DAYS_PER_WEEK - 1));

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [apptRes, hoursRes, excRes, typesRes, settingsRes] = await Promise.all([
        fetchAppointments(rangeFrom, rangeTo, null, 1, 200),
        fetchBusinessHours(),
        fetchAvailabilityExceptions(rangeFrom, rangeTo),
        fetchAppointmentTypes(),
        fetchSchedulingSettings(),
      ]);
      setAppointments((apptRes && apptRes.results) || []);
      setBusinessHours(hoursRes || []);
      setExceptions(excRes || []);
      setAppointmentTypes(typesRes || []);
      setSchedulingSettings(settingsRes || null);
    } catch (err) {
      console.error('Error loading scheduling data:', err);
      setError('Failed to load scheduling data.');
    } finally {
      setLoading(false);
    }
  }, [rangeFrom, rangeTo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && viewMode === 'week') {
      setViewMode('day');
    }
  }, [isMobile, viewMode]);

  const navStep = viewMode === 'day' ? 1 : 7;
  const goToPrevious = () => setAnchorDate((prev) => {
    let next = addDays(prev, -navStep);
    if (viewMode === 'day') {
      while (isWeekend(next)) next = addDays(next, -1);
    }
    return next;
  });
  const goToNext = () => setAnchorDate((prev) => {
    let next = addDays(prev, navStep);
    if (viewMode === 'day') {
      while (isWeekend(next)) next = addDays(next, 1);
    }
    return next;
  });
  const goToToday = () => {
    let today = new Date();
    if (viewMode === 'day') {
      while (isWeekend(today)) today = addDays(today, 1);
    }
    setAnchorDate(today);
  };

  const hoursForDay = (date) => {
    const iso = toISODate(date);
    const exception = exceptions.find((e) => e.date === iso);
    if (exception) {
      if (exception.is_closed) return null;
      if (exception.open_time && exception.close_time) {
        return { open: exception.open_time, close: exception.close_time, exception: true };
      }
    }
    const pyWeekday = (date.getDay() + 6) % 7; // Monday = 0, matches backend
    const bh = businessHours.find((h) => h.weekday === pyWeekday);
    if (!bh || bh.is_closed || !bh.open_time || !bh.close_time) return null;
    return { open: bh.open_time, close: bh.close_time, exception: false };
  };

  const handleOpenSlotClick = (date, hour, minutePart) => {
    const start = new Date(date);
    start.setHours(hour, minutePart, 0, 0);
    setPrefilledStart(start);
    setSelectedAppointment(null);
    setShowAppointmentModal(true);
  };

  const handleAppointmentClick = (appointment, e) => {
    if (e) e.stopPropagation();
    setSelectedAppointment(appointment);
    setPrefilledStart(null);
    setShowAppointmentModal(true);
  };

  const handleNewAppointmentClick = () => {
    setSelectedAppointment(null);
    setPrefilledStart(null);
    setShowAppointmentModal(true);
  };

  const handleModalClose = (didChange) => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
    setPrefilledStart(null);
    if (didChange) loadData();
  };

  const toggleTypeFilter = (typeId) => {
    setSelectedTypeIds((prev) => (
      prev.includes(typeId) ? prev.filter((id) => id !== typeId) : [...prev, typeId]
    ));
  };

  // Arrived/cancelled appointments stay visible (shaded via SHADED_STATUSES) rather
  // than being dropped from the calendar — only the service-type filter narrows this.
  const visibleAppointments = appointments.filter((a) => (
    selectedTypeIds.length === 0 || selectedTypeIds.includes(a.appointment_type)
  ));

  const typeColorMap = buildTypeColorMap(appointmentTypes);

  const hourRows = [];
  for (let h = DAY_START_HOUR; h < DAY_END_HOUR; h++) {
    hourRows.push(h);
  }

  // Bucket each displayed day's appointments once, then size every half-hour row to fit
  // whichever day has the most simultaneous appointments in it — shared across all columns
  // so the week grid stays aligned, and rows only grow where a conflict actually exists.
  const dayApptsByIndex = dayColumns.map((date) => (
    visibleAppointments
      .filter((a) => toISODate(new Date(a.start_datetime)) === toISODate(date))
      .map((a) => ({ appt: a, start: new Date(a.start_datetime), end: new Date(a.end_datetime) }))
      .sort((a, b) => a.start - b.start)
  ));

  const perDayRowCounts = dayApptsByIndex.map((list) => countRowOverlaps(list));
  const rowHeights = [];
  for (let r = 0; r < TOTAL_ROWS; r += 1) {
    const maxCount = Math.max(1, ...perDayRowCounts.map((counts) => counts[r]));
    rowHeights.push(BASE_ROW_HEIGHT * maxCount + STACK_GAP * (maxCount - 1));
  }
  const cumulativeTop = [0];
  for (let r = 0; r < TOTAL_ROWS; r += 1) {
    cumulativeTop.push(cumulativeTop[r] + rowHeights[r]);
  }
  const totalHeight = cumulativeTop[TOTAL_ROWS];

  const offsetForMinutes = (minutesFromDayStart) => {
    const clamped = Math.max(0, Math.min(minutesFromDayStart, TOTAL_ROWS * ROW_MINUTES));
    const rowIdx = Math.min(TOTAL_ROWS - 1, Math.floor(clamped / ROW_MINUTES));
    const fraction = Math.min(1, (clamped - rowIdx * ROW_MINUTES) / ROW_MINUTES);
    return cumulativeTop[rowIdx] + fraction * rowHeights[rowIdx];
  };
  const offsetForTime = (dateObj) => offsetForMinutes(minutesSinceMidnight(dateObj) - DAY_START_HOUR * 60);
  const offsetForTimeStr = (timeStr) => {
    const [h, m] = timeStr.split(':').map(Number);
    return offsetForMinutes((h - DAY_START_HOUR) * 60 + m);
  };

  const renderDayColumn = (date, dayIdx) => {
    const hours = hoursForDay(date);
    const dayAppointments = dayApptsByIndex[dayIdx];

    // Stack appointments that overlap in time directly below one another (full width, no
    // overlap) — the rows above have already grown to fit the day's busiest slot, so each
    // stacked block lands inside the lengthened row instead of spilling into the next one.
    let stackBottom = -Infinity;
    let chainEnd = null;
    const positionedAppointments = dayAppointments.map(({ appt, start, end }) => {
      const naturalTop = offsetForTime(start);
      // Sized from real duration at normal (unexpanded) scale — a row growing to fit several
      // appointments should not make each individual appointment's block any taller.
      const durationMinutes = (end - start) / 60000;
      const height = Math.max(20, (durationMinutes / ROW_MINUTES) * BASE_ROW_HEIGHT);
      const overlapsChain = chainEnd !== null && start < chainEnd;
      const top = overlapsChain ? stackBottom + STACK_GAP : Math.max(naturalTop, stackBottom);
      stackBottom = top + height;
      chainEnd = overlapsChain ? new Date(Math.max(chainEnd.getTime(), end.getTime())) : end;
      return { appt, top, height };
    });

    return (
      <div key={dayIdx} className="scheduling-day-column" style={{ height: totalHeight }}>
        {hourRows.map((h) => {
          const rowA = (h - DAY_START_HOUR) * ROWS_PER_HOUR;
          const rowB = rowA + 1;
          return (
            <div
              key={h}
              className="scheduling-hour-slot"
              style={{ height: rowHeights[rowA] + rowHeights[rowB] }}
            >
              <div
                className="scheduling-half-hour"
                style={{ height: rowHeights[rowA] }}
                onClick={() => handleOpenSlotClick(date, h, 0)}
              />
              <div
                className="scheduling-half-hour"
                style={{ height: rowHeights[rowB] }}
                onClick={() => handleOpenSlotClick(date, h, 30)}
              />
            </div>
          );
        })}

        {!hours && <div className="scheduling-closed-overlay">Closed</div>}
        {hours && (
          <>
            <div
              className="scheduling-closed-overlay scheduling-before-open"
              style={{ height: Math.max(0, offsetForTimeStr(hours.open)) }}
            />
            <div
              className="scheduling-closed-overlay scheduling-after-close"
              style={{ top: offsetForTimeStr(hours.close) }}
            />
          </>
        )}

        {positionedAppointments.map(({ appt, top, height }) => (
          <div
            key={appt.id}
            className={`scheduling-appointment-block ${SHADED_STATUSES.includes(appt.status) ? 'scheduling-appointment-shaded' : ''}`}
            style={{
              top,
              height,
              backgroundColor: typeColorMap[appt.appointment_type] || '#666',
              borderLeft: `4px solid ${STATUS_COLORS[appt.status] || '#666'}`,
            }}
            onClick={(e) => handleAppointmentClick(appt, e)}
            title={`${appt.customer_name} — ${appt.appointment_type_name} (${appt.status})`}
          >
            <div className="scheduling-appointment-time">
              {new Date(appt.start_datetime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </div>
            <div className="scheduling-appointment-name">{appt.customer_name}</div>
            <div className="scheduling-appointment-type">{appt.appointment_type_name}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderGrid = () => (
    <div className="scheduling-grid-wrapper">
      <div className="scheduling-grid" style={{ gridTemplateColumns: `60px repeat(${dayColumns.length}, minmax(190px, 1fr))` }}>
        <div className="scheduling-grid-corner" />
        {dayColumns.map((date, i) => {
          const isToday = toISODate(date) === toISODate(new Date());
          const label = viewMode === 'day'
            ? date.toLocaleDateString(undefined, { weekday: 'short' })
            : WEEKDAY_LABELS[i];
          return (
            <div key={i} className={`scheduling-day-header ${isToday ? 'is-today' : ''}`}>
              <div className="scheduling-day-label">{label}</div>
              <div className="scheduling-day-date">{date.getDate()}</div>
            </div>
          );
        })}

        <div className="scheduling-time-column">
          {hourRows.map((h) => {
            const rowA = (h - DAY_START_HOUR) * ROWS_PER_HOUR;
            const rowB = rowA + 1;
            return (
              <div key={h} className="scheduling-time-label" style={{ height: rowHeights[rowA] + rowHeights[rowB] }}>
                {h % 12 === 0 ? 12 : h % 12}{h < 12 ? 'am' : 'pm'}
              </div>
            );
          })}
        </div>

        {dayColumns.map((date, dayIdx) => renderDayColumn(date, dayIdx))}
      </div>
    </div>
  );

  const renderList = () => {
    const sorted = [...visibleAppointments].sort(
      (a, b) => new Date(a.start_datetime) - new Date(b.start_datetime)
    );
    const groups = [];
    sorted.forEach((appt) => {
      const dateKey = toISODate(new Date(appt.start_datetime));
      let group = groups.find((g) => g.dateKey === dateKey);
      if (!group) {
        group = { dateKey, items: [] };
        groups.push(group);
      }
      group.items.push(appt);
    });

    return (
      <div className="scheduling-list-wrapper">
        {groups.length === 0 && (
          <div className="scheduling-list-empty">No appointments in this range.</div>
        )}
        {groups.map((group) => (
          <div key={group.dateKey} className="scheduling-list-group">
            <div className="scheduling-list-date-header">
              {new Date(`${group.dateKey}T00:00:00`).toLocaleDateString(undefined, {
                weekday: 'long', month: 'short', day: 'numeric',
              })}
            </div>
            {group.items.map((appt) => {
              const start = new Date(appt.start_datetime);
              const end = new Date(appt.end_datetime);
              return (
                <div
                  key={appt.id}
                  className={`scheduling-list-item ${SHADED_STATUSES.includes(appt.status) ? 'scheduling-appointment-shaded' : ''}`}
                  onClick={() => handleAppointmentClick(appt)}
                >
                  <span
                    className="scheduling-list-status-dot"
                    style={{ backgroundColor: typeColorMap[appt.appointment_type] || '#666' }}
                  />
                  <span className="scheduling-list-time">
                    {start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    {' – '}
                    {end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </span>
                  <span className="scheduling-list-name">{appt.customer_name}</span>
                  <span className="scheduling-list-type">{appt.appointment_type_name}</span>
                  <span className={`scheduling-list-badge status-${appt.status}`}>{appt.status.replace('_', ' ')}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const rangeLabel = () => {
    if (viewMode === 'day') {
      return anchorDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    }
    return (
      <>
        {weekDays[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
        {' – '}
        {weekDays[weekDays.length - 1].toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
      </>
    );
  };

  return (
    <div className="scheduling-calendar">
      <div className="scheduling-toolbar">
        <div className="scheduling-toolbar-nav">
          <button className="scheduling-nav-btn" onClick={goToPrevious}>‹</button>
          <button className="scheduling-today-btn" onClick={goToToday}>Today</button>
          <button className="scheduling-nav-btn" onClick={goToNext}>›</button>
          <span className="scheduling-week-label">{rangeLabel()}</span>
        </div>
        <div className="scheduling-toolbar-actions">
          <div className="scheduling-view-toggle">
            {VIEW_MODES.filter((v) => !isMobile || v.key !== 'week').map((v) => (
              <button
                key={v.key}
                className={`scheduling-view-btn ${viewMode === v.key ? 'active' : ''}`}
                onClick={() => {
                  setViewMode(v.key);
                  if (v.key === 'day' && isWeekend(anchorDate)) {
                    let snapped = anchorDate;
                    while (isWeekend(snapped)) snapped = addDays(snapped, 1);
                    setAnchorDate(snapped);
                  }
                }}
              >
                {v.label}
              </button>
            ))}
          </div>
          <div className="scheduling-type-filter">
            <button
              className="scheduling-nav-btn"
              onClick={() => setShowTypeFilter((s) => !s)}
            >
              {selectedTypeIds.length === 0 ? 'All Services' : `${selectedTypeIds.length} Service${selectedTypeIds.length > 1 ? 's' : ''}`} ▾
            </button>
            {showTypeFilter && (
              <div className="scheduling-type-filter-dropdown">
                <div
                  className="scheduling-type-filter-option scheduling-type-filter-clear"
                  onClick={() => setSelectedTypeIds([])}
                >
                  Show all
                </div>
                {appointmentTypes.map((t) => (
                  <label key={t.id} className="scheduling-type-filter-option">
                    <input
                      type="checkbox"
                      checked={selectedTypeIds.includes(t.id)}
                      onChange={() => toggleTypeFilter(t.id)}
                    />
                    <span
                      className="scheduling-type-color-dot"
                      style={{ backgroundColor: typeColorMap[t.id] }}
                    />
                    {t.name}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button className="scheduling-new-btn" onClick={handleNewAppointmentClick}>
            New Appointment +
          </button>
          <button className="scheduling-settings-btn" onClick={() => setShowSettingsModal(true)} title="Scheduling settings">
            ⚙
          </button>
        </div>
      </div>

      {error && <div className="scheduling-error">{error}</div>}

      {viewMode === 'list' ? renderList() : renderGrid()}

      {loading && <div className="scheduling-loading">Loading…</div>}

      {showAppointmentModal && (
        <AppointmentModal
          appointment={selectedAppointment}
          prefilledStart={prefilledStart}
          appointmentTypes={appointmentTypes}
          slotIntervalMinutes={schedulingSettings ? schedulingSettings.slot_interval_minutes : 30}
          onClose={handleModalClose}
        />
      )}

      {showSettingsModal && (
        <SchedulingSettingsModal onClose={() => { setShowSettingsModal(false); loadData(); }} />
      )}
    </div>
  );
};

export default SchedulingCalendar;
